import { ErrorMessage, useFormikContext } from "formik";
import { PropsStep } from "../../utils";
import { FC, useEffect, useState } from "react";
import GenericTooltip from "../../../../hooks/utils/GenericTooltip";
import { KTSVG } from "../../../../../_metronic/helpers";
import { useHistory } from "react-router-dom";
import { toInt } from "../../../../utils/inputValidations";
import { GuestsAutocompleteField, RelativeItem } from "../fields/GuestsAutocompleteField";
import { formatDateStrShort, getOnlyDate } from "../../../../helpers/FormatDate";
import { formatUsdAmount } from "../../../../utils/dateValidations";

export const Step1: FC<PropsStep> = ({ experience, step, onNextStep, maxGuests }) => {
    const formik = useFormikContext<any>()
    const history = useHistory()
    const [relativesList, setRelativesList] = useState<RelativeItem[]>([])

    const isGroupPrice = experience?.priceType === 2
    const effectiveMaxGuests = isGroupPrice && experience?.maxGuestsPerGroup ? Number(experience.maxGuestsPerGroup) : maxGuests

    useEffect(() => {
        if (experience?.id) {
            formik?.setFieldValue('audience_type', experience?.audienceType);
            formik?.setFieldValue('experience_id', experience?.id);
            calculateTotals();
        }
    }, [experience]);

    useEffect(() => {
        calculateTotals();
    }, [formik.values.enhancements_data]);

    const validateOnChange = (e: any, field: string) => {
        const value = e.target.value;
        // Permitir vacío mientras escribe
        if (value === "") {
            formik.setFieldValue(field, "");
            return;
        }
        // Solo números
        if (!/^\d+$/.test(value)) return;
        formik.setFieldValue(field, value);
    };

    const validateOnBlur = (e: any, field: string, min: number = 0, max: number = effectiveMaxGuests) => {
        let value = Number(e.target.value);
        if (isNaN(value)) value = min;
        if (value > max) value = max;
        if (value < min) value = min;
        formik.setFieldValue(field, value);
        // ejecutamos los totales
        setTimeout(() => calculateTotals(), 0);
    }

    const handleChangeGuests = (values: number[]) => {
        formik.setFieldValue('guests', values);
        formik.setFieldTouched('guests', true);
        syncAttendees(values, relativesList);
    }

    const handleRelativesLoaded = (list: RelativeItem[]) => {
        setRelativesList(list);
        if (formik.values?.guests?.length > 0) {
            syncAttendees(formik.values.guests, list);
        }
    }

    const syncAttendees = (guestIds: number[], relatives: RelativeItem[]) => {
        const selected = relatives.filter((r) => guestIds.includes(r.id));
        const attendees_ = selected.map((r) => ({
            relative_id: r.id,
            name: r.first_name || '',
            last_name: r.last_name || '',
            birthday: r.year_of_birth ? `${r.year_of_birth}-01-01` : '',
            gender: '',
            country: '',
            phone_number: '',
        }));
        formik.setFieldValue('attendees', attendees_);
    }

    const calculateTotals = () => {
        const optional = formik.values?.enhancements_data?.reduce((sum: number, item: any) => sum + Number(item.price || 0), 0);
        const sum = toInt(formik.values?.guest_capacity_adults ?? 1) + toInt(formik.values?.guest_capacity_children) + toInt(formik.values?.guest_capacity_youth) + toInt(formik.values?.guest_capacity_infants);
        
        let subTotal_ = 0;
        if (isGroupPrice) {
            // Por grupo: el precio base y los opcionales se cobran x1
            subTotal_ = 1 * Number(experience?.pricePerGuest || 0) + 1 * Number(optional || 0);
        } else {
            // Por invitado: precio base y opcionales x cantidad de invitados
            subTotal_ = (sum || 1) * Number(experience?.pricePerGuest || 0) + (sum || 1) * Number(optional || 0);
        }

        formik.setFieldValue('subtotal', subTotal_)
        const tax_ = 0;
        formik.setFieldValue('tax', tax_)
        const total_ = subTotal_ + tax_;
        formik.setFieldValue('total', total_)
        formik.setFieldValue('guests_count', sum)
    }

    // Cálculos de requerimiento de familiares
    const adultsNeeded = Math.max(0, toInt(formik.values?.guest_capacity_adults ?? 1) - 1);
    const youthNeeded = toInt(formik.values?.guest_capacity_youth);
    const childrenNeeded = toInt(formik.values?.guest_capacity_children);
    const infantsNeeded = toInt(formik.values?.guest_capacity_infants);
    const totalRelativesNeeded = adultsNeeded + youthNeeded + childrenNeeded + infantsNeeded;

    // Conteo por categoría de los familiares seleccionados
    const selectedRelatives = (formik.values?.guests || [])
        .map((id: number) => relativesList.find((r) => r.id === id))
        .filter(Boolean) as RelativeItem[];

    const selectedAdults = selectedRelatives.filter(r => r.category === 'adult').length;
    const selectedYouth = selectedRelatives.filter(r => r.category === 'youth').length;
    const selectedChildren = selectedRelatives.filter(r => r.category === 'child').length;
    const selectedInfants = selectedRelatives.filter(r => r.category === 'infant').length;

    const diffAdults = adultsNeeded - selectedAdults;
    const diffYouth = youthNeeded - selectedYouth;
    const diffChildren = childrenNeeded - selectedChildren;
    const diffInfants = infantsNeeded - selectedInfants;

    const pendingItems: string[] = [];
    if (diffAdults > 0) pendingItems.push(`${diffAdults} adult${diffAdults > 1 ? 's' : ''}`);
    if (diffYouth > 0) pendingItems.push(`${diffYouth} youth`);
    if (diffChildren > 0) pendingItems.push(`${diffChildren} child${diffChildren > 1 ? 'ren' : ''}`);
    if (diffInfants > 0) pendingItems.push(`${diffInfants} infant${diffInfants > 1 ? 's' : ''}`);

    const excessItems: string[] = [];
    if (diffAdults < 0) excessItems.push(`${Math.abs(diffAdults)} extra adult${Math.abs(diffAdults) > 1 ? 's' : ''}`);
    if (diffYouth < 0) excessItems.push(`${Math.abs(diffYouth)} extra youth`);
    if (diffChildren < 0) excessItems.push(`${Math.abs(diffChildren)} extra child${Math.abs(diffChildren) > 1 ? 'ren' : ''}`);
    if (diffInfants < 0) excessItems.push(`${Math.abs(diffInfants)} extra infant${Math.abs(diffInfants) > 1 ? 's' : ''}`);

    const hasMismatch = excessItems.length > 0;
    const isComplete = totalRelativesNeeded > 0 && selectedRelatives.length === totalRelativesNeeded && !hasMismatch;

    return (<>
        <section className="request-page__content row">
            <div className='d-flex'>
                <div className="experience-preview-leadbar-btn-secondary" onClick={() => history.goBack()}>
                    <div className="experience-preview-leadbar-btn-text tap-flex-center w-100">
                        <div>go back&nbsp;&nbsp;</div>
                        <KTSVG path="/media/svg/nobilis/arrow-right-white.svg" />
                    </div>
                </div>
            </div>
            <header className="request-page__header">
                <h1>Send a Request to {experience?.host?.firstName} {experience?.host?.lastName}</h1>
            </header>
            <div className='w-100 d-flex row gap-12'>
                {/* ATTENDEES */}
                <div className="request-block">
                    <span className="request-block__label">Attendees</span>

                    <div className="request-attendees">
                        <div className="request-attendees__toggle">
                            <span>
                                {experience?.audienceType == 2 ? 'Adults Only' : 'Family Welcome'}
                                {isGroupPrice ? ` (Group pricing: max ${effectiveMaxGuests} guests)` : ''}
                            </span>
                            <span className="experience-preview-icon-default">
                                <GenericTooltip
                                    title={``}
                                    message={
                                        experience?.audienceType == 2
                                            ? "This experience welcomes adult members only, aged 18 and above."
                                            : (isGroupPrice
                                                ? `This is a group experience with a maximum of ${effectiveMaxGuests} guests.`
                                                : "This experience welcomes children and family members of all ages. Infants under 2 are free of charge.")
                                    }
                                    position="top"
                                    theme="light"
                                    iconPath={'/media/svg/nobilis/info.svg'}
                                />
                            </span>
                        </div>
                        <section className="request-attendees__box tap-add-experience-2-section tap-flex-center">
                            <div className="tap-add-experience-2-capacity-row w-100">
                                <div className={`tap-add-experience-2-capacity-col ${formik.values.audience_type == 1 || formik.values.audience_type == 2 ? 'd-flex' : 'd-none'}`}>
                                    <label className="tap-add-experience-2-label">Adults</label>
                                    <div className="tap-add-experience-2-input-line">
                                        <input type='number' name="guest_capacity_adults" className="subscribe-input" placeholder='Adults' min={1} max={effectiveMaxGuests}
                                            value={formik.values.guest_capacity_adults ?? 1}
                                            onChange={(e) => validateOnChange(e, 'guest_capacity_adults')}
                                            onBlur={(e) => validateOnBlur(e, 'guest_capacity_adults', 1, effectiveMaxGuests)} autoComplete="off" />
                                    </div>
                                    <div className="tap-add-experience-2-input-hint">Ages 18+</div>
                                    <ErrorMessage name={`guest_capacity_adults`} component="div" className="tap-add-experience-text text-danger" />
                                </div>
                                <div className={`tap-add-experience-2-capacity-col ${formik.values.audience_type == 1 ? 'd-flex' : 'd-none'}`}>
                                    <label className="tap-add-experience-2-label">Youth</label>
                                    <div className="tap-add-experience-2-input-line">
                                        <input type='number' name="guest_capacity_youth" className="subscribe-input" placeholder='Youth' min={0} max={effectiveMaxGuests}
                                            value={formik.values.guest_capacity_youth ?? 0}
                                            onChange={(e) => validateOnChange(e, 'guest_capacity_youth')}
                                            onBlur={(e) => validateOnBlur(e, 'guest_capacity_youth', 0, effectiveMaxGuests)} autoComplete="off" />
                                    </div>
                                    <div className="tap-add-experience-2-input-hint">Ages 13-17</div>
                                    <ErrorMessage name={`guest_capacity_youth`} component="div" className="tap-add-experience-text text-danger" />
                                </div>
                                <div className={`tap-add-experience-2-capacity-col ${formik.values.audience_type == 1 ? 'd-flex' : 'd-none'}`}>
                                    <label className="tap-add-experience-2-label">Children</label>
                                    <div className="tap-add-experience-2-input-line">
                                        <input type='number' name="guest_capacity_children" className="subscribe-input" placeholder='Children' min={0} max={effectiveMaxGuests}
                                            value={formik.values.guest_capacity_children ?? 0}
                                            onChange={(e) => validateOnChange(e, 'guest_capacity_children')}
                                            onBlur={(e) => validateOnBlur(e, 'guest_capacity_children', 0, effectiveMaxGuests)} autoComplete="off" />
                                    </div>
                                    <div className="tap-add-experience-2-input-hint">Ages 2-12</div>
                                    <ErrorMessage name={`guest_capacity_children`} component="div" className="tap-add-experience-text text-danger" />
                                </div>
                                <div className={`tap-add-experience-2-capacity-col ${formik.values.audience_type == 1 ? 'd-flex' : 'd-none'}`}>
                                    <label className="tap-add-experience-2-label">Infants</label>
                                    <div className="tap-add-experience-2-input-line">
                                        <input type='number' name="guest_capacity_infants" className="subscribe-input" placeholder='Infants' min={0} max={effectiveMaxGuests}
                                            value={formik.values.guest_capacity_infants ?? 0}
                                            onChange={(e) => validateOnChange(e, 'guest_capacity_infants')}
                                            onBlur={(e) => validateOnBlur(e, 'guest_capacity_infants', 0, effectiveMaxGuests)} autoComplete="off" />
                                    </div>
                                    <div className="tap-add-experience-2-input-hint">Under 2</div>
                                    <ErrorMessage name={`guest_capacity_infants`} component="div" className="tap-add-experience-text text-danger" />
                                </div>
                            </div>
                        </section>
                    </div>
                </div>

                {/* GUEST LIST (FAMILY RELATIVES) */}
                {totalRelativesNeeded > 0 && (
                    <div className="request-block">
                        <label className="request-block__label">
                            Guests list ({selectedRelatives.length}/{totalRelativesNeeded} selected)
                        </label>
                        <p className="request-helper" style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>
                            You count as 1 adult. Please select {totalRelativesNeeded} relative(s) from your profile:
                            {adultsNeeded > 0 ? ` ${adultsNeeded} Adult(s)` : ''}
                            {youthNeeded > 0 ? ` ${youthNeeded} Youth` : ''}
                            {childrenNeeded > 0 ? ` ${childrenNeeded} Children` : ''}
                            {infantsNeeded > 0 ? ` ${infantsNeeded} Infant(s)` : ''}.
                        </p>
                        <GuestsAutocompleteField
                            values={formik.values?.guests || []}
                            onChange={handleChangeGuests}
                            experience={experience}
                            maxItems={totalRelativesNeeded}
                            onRelativesLoaded={handleRelativesLoaded}
                        />

                        {/* Real-time Category Status & Hints */}
                        <div className="mt-2" style={{ fontSize: '13px' }}>
                            {pendingItems.length > 0 && (
                                <div style={{ color: '#aaa' }}>
                                    <span style={{ fontWeight: 600 }}>To select:</span> {pendingItems.join(', ')}
                                </div>
                            )}
                            {hasMismatch && (
                                <div className="text-danger mt-1">
                                    <span style={{ fontWeight: 600 }}>Category mismatch:</span> {excessItems.join(', ')} selected. Please remove and select the appropriate age category.
                                </div>
                            )}
                            {isComplete && (
                                <div style={{ color: '#4caf50' }} className="mt-1">
                                    ✓ All selected relatives match your guest counts.
                                </div>
                            )}
                        </div>

                        <div className="input-line input-line--icon" />
                        <ErrorMessage name={`guests`} component="div" className="tap-add-experience-text text-danger" />
                    </div>
                )}

                {/* TOPIC */}
                <div className="request-block">
                    <label className="request-block__label">Topic</label>
                    <div className="tap-add-experience-2-input-line">
                        <input type='text' name="topic" maxLength={1000} className="subscribe-input" placeholder='Topic' autoComplete="off"
                            value={formik.values?.topic ?? ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur} />
                    </div>
                    <ErrorMessage name={`topic`} component="div" className="tap-add-experience-text text-danger" />
                </div>

                {/* MESSAGE */}
                <div className="request-block">
                    <label className="request-block__label">Message to Host</label>
                    <p className="request-helper">
                        Share your reason for joining and any details the Host should consider
                    </p>

                    <div className="textarea">
                        <div className="tap-add-experience-2-input-line">
                            <input type='text' name="message" maxLength={1000} className="subscribe-input" placeholder='Message' autoComplete="off"
                                value={formik.values?.message ?? ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur} />
                        </div>
                        <span className="textarea__count">{formik.values.message?.length ?? 0}/100</span>
                        <ErrorMessage name={`message`} component="div" className="tap-add-experience-text text-danger" />
                    </div>
                </div>

                {/* date */}
                <div className="request-block">
                    <label className="request-block__label">Event Date</label>
                    <div className="textarea">
                        <div className="tap-add-experience-2-input-line">
                            <select name="date_id" value={formik.values.date_id} onChange={formik.handleChange} className='tap-select-add-experience select-black w-100 text-white pl_0' placeholder='Select an option'>
                                <option value="" defaultChecked>Select a date</option>
                                {(experience?.dates ?? []).map((d:any,index:number)=>
                                    (<option key={`date-${index}`} value={d.id}>{formatDateStrShort(d.start)}</option>)
                                )}
                            </select>
                        </div>
                        <ErrorMessage name={`date_id`} component="div" className="tap-add-experience-text text-danger" />
                    </div>
                </div>
            </div>
        </section>

        {/* RIGHT SIDEBAR */}
        <aside className="request-summary">
            <div className="request-summary__prices">
                <div className="price-row">
                    <span>Experience fee {isGroupPrice ? '(Group)' : ''}</span>
                    <strong>{formatUsdAmount(experience?.pricePerGuest)}</strong>
                </div>
                {(experience.enhancements || []).map((enhancement: any) => {
                    const enhancements = formik.values.enhancements_data || [];
                    const isChecked = enhancements.some((item: any) => item.id === enhancement.id);

                    const handleChange = (checked: boolean) => {
                        const updated = checked
                            ? [
                                ...enhancements.filter((e: any) => e.id !== enhancement.id),
                                { id: enhancement.id, price: Number(enhancement.price), name: enhancement.name},
                            ]
                            : enhancements.filter((e: any) => e.id !== enhancement.id);
                        formik.setFieldValue("enhancements_data", updated);
                    };

                    return (
                        <div className="price-row" key={enhancement.id}>
                            <input type="checkbox" className="pf-check" id={`optional-${enhancement.id}`} checked={isChecked} onChange={(e) => handleChange(e.target.checked)}/>
                            <label htmlFor={`optional-${enhancement.id}`} className="w-75">
                                {enhancement.name} {isGroupPrice ? '(Group)' : ''}
                            </label>
                            <strong>{formatUsdAmount(parseInt(enhancement.price, 10))}</strong>
                        </div>
                    );
                })}
                <div className="price-row">
                    <span>Subtotal</span>
                    <strong>{formatUsdAmount(formik.values?.subtotal)}</strong>
                </div>
                <div className="price-row">
                    <span>Tax</span>
                    <strong>{formatUsdAmount(formik.values?.tax)}</strong>
                </div>
                <div className="price-row price-row--total">
                    <span>Total</span>
                    <strong>{formatUsdAmount(formik.values?.total)}</strong>
                </div>
            </div>

            <div className="terms">
                <input type="checkbox" name='term_condition' id="term-condition" className="pf-check" checked={formik.values.term_condition} onChange={() => formik.setFieldValue('term_condition', !formik.values.term_condition)} />
                <label htmlFor={`term-condition`}>
                    <p>
                        By completing this payment, you agree to the Nobilis Membership Terms
                        & Conditions
                    </p>
                </label>
            </div>
            <ErrorMessage name={`term_condition`} component="div" className="tap-add-experience-text text-danger" />

            <div onClick={onNextStep} className="tap-add-experience-2-btn-secondary tap-flex-center cursor-pointer">
                {formik.values.guests_count == 1 ? 'Secure my place' : 'Continue to payment'} &nbsp;<KTSVG path='/media/svg/nobilis/vector1.svg' />
            </div>

            <p className="request-summary__note">
                Please note: Payment is authorized on hold and will only be captured when the host accepts your request. If declined, the hold is released automatically.
            </p>
        </aside>
    </>);
}
